import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getAbsensiList, getSekolahList, getProxiedImageUrl } from '../../lib/api';
import { formatDate, formatTime, getStatusColor, getStatusLabel } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import ProtectedImage from '../ui/ProtectedImage';
import { 
  Calendar as CalendarIcon, ChevronLeft, ChevronDown, ChevronRight,
  MapPin, Clock, Image, Loader2, Building2, Users, CheckCircle2,
  AlertCircle, XCircle, User, Landmark
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';

const RiwayatPage = ({ orgType }) => {
  const { user, isAdminDinas, isKepalaSekolah, isPengawas, isPimpinan } = useAuth();
  const navigate = useNavigate();
  const [absensiList, setAbsensiList] = useState([]);
  const [sekolahList, setSekolahList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedAbsensi, setSelectedAbsensi] = useState(null);
  const [expandedSekolah, setExpandedSekolah] = useState(null);

  // For admin views (riwayat-guru, riwayat-instansi), show multi-school grouped view
  // For personal riwayat (no orgType), pimpinan/editor only see their own records
  const isAdminView = !!orgType;
  const canSeeMultipleSchools = isAdminView && (isAdminDinas || isPengawas || isPimpinan);

  useEffect(() => {
    loadData();
  }, [selectedDate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedDate) {
        params.tanggal = format(selectedDate, 'yyyy-MM-dd');
      }
      if (orgType) {
        params.org_type = orgType;
      }
      // For personal riwayat of pimpinan/editor, pass guru_id to filter own records
      if (!orgType && ['pimpinan', 'editor'].includes(user?.role)) {
        params.guru_id = user?.id;
      }
      
      const [absensiData, sekolahData] = await Promise.all([
        getAbsensiList(params),
        canSeeMultipleSchools ? getSekolahList() : Promise.resolve([])
      ]);
      
      setAbsensiList(absensiData);
      setSekolahList(sekolahData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Group absensi by school
  const groupedBySekolah = useMemo(() => {
    const groups = {};
    
    absensiList.forEach(absen => {
      // Group by sekolah_id or instansi_id
      const groupId = absen.sekolah_id || absen.instansi_id || 'unknown';
      const groupNama = absen.sekolah_nama || absen.instansi_nama || 'Unknown';
      if (!groups[groupId]) {
        groups[groupId] = {
          sekolah_id: groupId,
          sekolah_nama: groupNama,
          is_instansi: !!absen.instansi_id && !absen.sekolah_id,
          absensi: [],
          stats: {
            total: 0,
            hadir_tepat_waktu: 0,
            hadir_terlambat: 0,
            mangkir: 0,
            dalam_radius: 0,
            luar_radius: 0
          }
        };
      }
      
      groups[groupId].absensi.push(absen);
      groups[groupId].stats.total++;
      
      if (absen.status === 'hadir_tepat_waktu') {
        groups[groupId].stats.hadir_tepat_waktu++;
      } else if (absen.status === 'hadir_terlambat') {
        groups[groupId].stats.hadir_terlambat++;
      } else {
        groups[groupId].stats.mangkir++;
      }
      
      if (absen.dalam_radius) {
        groups[groupId].stats.dalam_radius++;
      } else {
        groups[groupId].stats.luar_radius++;
      }
    });
    
    return Object.values(groups).sort((a, b) => a.sekolah_nama.localeCompare(b.sekolah_nama));
  }, [absensiList]);

  const toggleSekolah = (sekolahId) => {
    setExpandedSekolah(expandedSekolah === sekolahId ? null : sekolahId);
  };

  const clearFilter = () => {
    setSelectedDate(new Date());
  };

  // For single school view (Guru, Kepala Sekolah)
  const renderSingleSchoolView = () => (
    <div className="space-y-4">
      {absensiList.map((absen) => (
        <Card 
          key={absen.id} 
          className="border-indigo-100/50 cursor-pointer card-hover"
          onClick={() => setSelectedAbsensi(absen)}
          data-testid={`absensi-card-${absen.id}`}
        >
          <CardContent className="p-4">
            <div className="flex gap-4">
              {/* Photo Thumbnail */}
              <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                {absen.foto_url ? (
                  <ProtectedImage 
                    src={getProxiedImageUrl(absen.foto_url)} 
                    alt="Foto absensi"
                    className="w-full h-full"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Image className="w-8 h-8 text-gray-300" />
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                {/* Guru Name - NEW */}
                <div className="flex items-center gap-2 mb-1">
                  <User className="w-4 h-4 text-indigo-600" />
                  <span className="font-semibold text-indigo-950">{absen.guru_nama}</span>
                </div>

                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm text-gray-600 capitalize">
                    Absen {absen.tipe}
                  </span>
                  <Badge className={`text-xs ${getStatusColor(absen.status)}`}>
                    {getStatusLabel(absen.status)}
                  </Badge>
                </div>

                <div className="flex items-center gap-4 text-sm text-gray-500 mb-2">
                  <span className="flex items-center gap-1">
                    <CalendarIcon className="w-3.5 h-3.5" />
                    {formatDate(absen.waktu_absen)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    {formatTime(absen.waktu_absen)}
                  </span>
                </div>

                {/* Organisasi - Sekolah or Instansi */}
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                  <Building2 className="w-3 h-3" />
                  <span>{absen.instansi_nama || absen.sekolah_nama}</span>
                </div>

                <div className="flex items-center gap-2 text-xs">
                  <MapPin className="w-3 h-3" />
                  <span className={absen.dalam_radius ? 'text-emerald-600' : 'text-red-600'}>
                    {absen.dalam_radius ? 'Dalam radius lokasi' : 'Di luar radius lokasi'}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  // For multiple schools view (Admin, Pengawas, Pimpinan)
  const renderMultiSchoolView = () => (
    <div className="space-y-4">
      {groupedBySekolah.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Building2 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium">Tidak ada data absensi</p>
          <p className="text-sm">Tidak ada absensi pada tanggal ini</p>
        </div>
      ) : (
        groupedBySekolah.map((group) => (
          <Card 
            key={group.sekolah_id} 
            className="border-indigo-100/50 overflow-hidden"
            data-testid={`sekolah-group-${group.sekolah_id}`}
          >
            {/* School Header - Clickable */}
            <CardHeader 
              className="cursor-pointer hover:bg-indigo-50/50 transition-colors py-4"
              onClick={() => toggleSekolah(group.sekolah_id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg ${group.is_instansi ? 'bg-blue-100' : 'bg-indigo-100'} flex items-center justify-center`}>
                    {group.is_instansi ? (
                      <Landmark className="w-5 h-5 text-blue-600" />
                    ) : (
                      <Building2 className="w-5 h-5 text-indigo-600" />
                    )}
                  </div>
                  <div>
                    <CardTitle className="text-base font-semibold text-indigo-950">
                      {group.sekolah_nama}
                    </CardTitle>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {group.stats.total} absensi
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  {/* Stats Badges */}
                  <div className="hidden md:flex items-center gap-2">
                    {group.stats.hadir_tepat_waktu > 0 && (
                      <Badge className="bg-emerald-100 text-emerald-800 text-xs">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        {group.stats.hadir_tepat_waktu} Tepat
                      </Badge>
                    )}
                    {group.stats.hadir_terlambat > 0 && (
                      <Badge className="bg-amber-100 text-amber-800 text-xs">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        {group.stats.hadir_terlambat} Terlambat
                      </Badge>
                    )}
                    {group.stats.luar_radius > 0 && (
                      <Badge className="bg-red-100 text-red-800 text-xs">
                        <XCircle className="w-3 h-3 mr-1" />
                        {group.stats.luar_radius} Luar Radius
                      </Badge>
                    )}
                  </div>
                  
                  {/* Expand Icon */}
                  {expandedSekolah === group.sekolah_id ? (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>

              {/* Mobile Stats */}
              <div className="flex md:hidden items-center gap-2 mt-2">
                {group.stats.hadir_tepat_waktu > 0 && (
                  <Badge className="bg-emerald-100 text-emerald-800 text-xs">
                    {group.stats.hadir_tepat_waktu} Tepat
                  </Badge>
                )}
                {group.stats.hadir_terlambat > 0 && (
                  <Badge className="bg-amber-100 text-amber-800 text-xs">
                    {group.stats.hadir_terlambat} Terlambat
                  </Badge>
                )}
                {group.stats.luar_radius > 0 && (
                  <Badge className="bg-red-100 text-red-800 text-xs">
                    {group.stats.luar_radius} Luar Radius
                  </Badge>
                )}
              </div>
            </CardHeader>

            {/* Expanded Teacher List */}
            {expandedSekolah === group.sekolah_id && (
              <CardContent className="pt-0 pb-4">
                <div className="border-t border-indigo-100 pt-4 space-y-3">
                  {group.absensi.map((absen) => (
                    <div 
                      key={absen.id}
                      className="flex gap-3 p-3 rounded-lg bg-gray-50 hover:bg-indigo-50/50 cursor-pointer transition-colors"
                      onClick={() => setSelectedAbsensi(absen)}
                      data-testid={`absensi-item-${absen.id}`}
                    >
                      {/* Photo */}
                      <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0">
                        {absen.foto_url ? (
                          <ProtectedImage 
                            src={getProxiedImageUrl(absen.foto_url)} 
                            alt="Foto"
                            className="w-full h-full"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <User className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-indigo-950 truncate">
                            {absen.guru_nama}
                          </span>
                          <Badge className={`text-xs flex-shrink-0 ${getStatusColor(absen.status)}`}>
                            {getStatusLabel(absen.status)}
                          </Badge>
                        </div>
                        
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatTime(absen.waktu_absen)}
                          </span>
                          <span className="capitalize">Absen {absen.tipe}</span>
                        </div>

                        <div className="flex items-center gap-1 mt-1 text-xs">
                          <MapPin className="w-3 h-3" />
                          <span className={absen.dalam_radius ? 'text-emerald-600' : 'text-red-600'}>
                            {absen.dalam_radius ? 'Dalam radius' : 'Luar radius'}
                          </span>
                          {absen.jarak_dari_sekolah && (
                            <span className="text-gray-400">
                              ({Math.round(absen.jarak_dari_sekolah)}m)
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        ))
      )}
    </div>
  );

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto" data-testid="riwayat-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-indigo-950">
            {orgType === 'instansi' ? 'Riwayat Absensi Instansi' : orgType === 'sekolah' ? 'Riwayat Absensi Guru' : 'Riwayat Absensi'}
          </h1>
          <p className="text-gray-500">
            {orgType === 'instansi' 
              ? 'Riwayat absensi pegawai instansi'
              : orgType === 'sekolah'
                ? 'Riwayat absensi guru/staff sekolah'
                : canSeeMultipleSchools 
                  ? 'Riwayat absensi semua sekolah' 
                  : 'Lihat riwayat absensi Anda'}
          </p>
        </div>
      </div>

      {/* Date Filter */}
      <Card className="mb-6 border-indigo-100/50">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <Popover>
              <PopoverTrigger asChild>
                <Button 
                  variant="outline" 
                  className="border-indigo-200 flex-1 justify-start"
                  data-testid="date-filter-btn"
                >
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {selectedDate ? format(selectedDate, 'dd MMMM yyyy', { locale: id }) : 'Pilih Tanggal'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {selectedDate && (
              <Button variant="ghost" onClick={clearFilter}>
                Hari Ini
              </Button>
            )}
          </div>
          
          {/* Summary Stats */}
          {!loading && absensiList.length > 0 && (
            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-indigo-100 text-sm">
              <span className="text-gray-500">
                Total: <span className="font-semibold text-indigo-950">{absensiList.length}</span> absensi
              </span>
              {canSeeMultipleSchools && (
                <span className="text-gray-500">
                  dari <span className="font-semibold text-indigo-950">{groupedBySekolah.filter(g => !g.is_instansi).length}</span> sekolah
                  {groupedBySekolah.some(g => g.is_instansi) && (
                    <> & <span className="font-semibold text-indigo-950">{groupedBySekolah.filter(g => g.is_instansi).length}</span> instansi</>
                  )}
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      ) : absensiList.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <CalendarIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium">Tidak ada data absensi</p>
          <p className="text-sm">
            {selectedDate ? 'Tidak ada absensi pada tanggal ini' : 'Riwayat absensi akan muncul di sini'}
          </p>
        </div>
      ) : canSeeMultipleSchools ? (
        renderMultiSchoolView()
      ) : (
        renderSingleSchoolView()
      )}

      {/* Detail Modal */}
      {selectedAbsensi && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedAbsensi(null)}
        >
          <Card 
            className="w-full max-w-md max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Detail Absensi</span>
                <Badge className={getStatusColor(selectedAbsensi.status)}>
                  {getStatusLabel(selectedAbsensi.status)}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Photo */}
              {selectedAbsensi.foto_url && (
                <div className="rounded-lg overflow-hidden bg-gray-100">
                  <ProtectedImage 
                    src={getProxiedImageUrl(selectedAbsensi.foto_url)} 
                    alt="Foto absensi"
                    className="w-full"
                  />
                </div>
              )}

              {/* Details */}
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Nama</span>
                  <span className="font-medium">{selectedAbsensi.guru_nama}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Sekolah</span>
                  <span className="font-medium">{selectedAbsensi.sekolah_nama}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Tipe</span>
                  <span className="font-medium capitalize">{selectedAbsensi.tipe}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Tanggal</span>
                  <span className="font-medium">{formatDate(selectedAbsensi.waktu_absen)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Waktu</span>
                  <span className="font-medium">{formatTime(selectedAbsensi.waktu_absen)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Koordinat</span>
                  <span className="font-mono text-xs">
                    {selectedAbsensi.latitude?.toFixed(6)}, {selectedAbsensi.longitude?.toFixed(6)}
                  </span>
                </div>
                {selectedAbsensi.akurasi_gps && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Akurasi GPS</span>
                    <span className="font-medium">{Math.round(selectedAbsensi.akurasi_gps)} meter</span>
                  </div>
                )}
                {selectedAbsensi.jarak_dari_sekolah && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Jarak dari Sekolah</span>
                    <span className="font-medium">{Math.round(selectedAbsensi.jarak_dari_sekolah)} meter</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">Dalam Radius</span>
                  <span className={`font-medium ${selectedAbsensi.dalam_radius ? 'text-emerald-600' : 'text-red-600'}`}>
                    {selectedAbsensi.dalam_radius ? 'Ya' : 'Tidak'}
                  </span>
                </div>
                {selectedAbsensi.face_verified !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Verifikasi Wajah</span>
                    <span className={`font-medium ${selectedAbsensi.face_verified ? 'text-emerald-600' : 'text-red-600'}`}>
                      {selectedAbsensi.face_verified ? 'Terverifikasi' : 'Tidak Terverifikasi'}
                    </span>
                  </div>
                )}
              </div>

              <Button 
                className="w-full" 
                variant="outline"
                onClick={() => setSelectedAbsensi(null)}
              >
                Tutup
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default RiwayatPage;
